import React, { useMemo } from 'react';
import styled, { css, keyframes } from 'styled-components';

import { Chip, Icon, formatNumber } from '@column/column-ui-kit';

import { CopyInput } from '~/components';
import { CheckScannerIcon } from '~/elements/icons/transfers/CheckScanner';
import { Flex, FormElement, FormLabel, Inner, Text } from '~/styles';
import { ACHTransferBatch } from '~/typings/API';
import { formatDate, formatDateTime } from '~/util';

import { buildBreakdownText, getBatchStatusDisplay, pickBatchAmounts } from './recordHelpers';
import { RecordsHoverText } from './RecordsHoverText';

const Summary = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 24px;

  @media (max-width: 900px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
`;

const Meta = styled.ul`
  display: flex;
  align-items: center;
  gap: 6px 16px;
  list-style: none;
  flex-wrap: wrap;
  margin: 0;
  padding: 0;

  li {
    display: flex;
    align-items: center;
  }
`;

const IdField = styled.div`
  min-width: 310px;
  max-width: 25%;

  @media (max-width: 900px) {
    min-width: 0;
    max-width: 100%;
  }
`;

const DateTimeDisplay = styled.div`
  position: relative;
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
`;

const DateTimeLocal = styled.span`
  transition: opacity 0.2s ease-in-out;
  opacity: 1;
  position: absolute;
  left: 0;
`;

const DateTimeUTC = styled(DateTimeLocal)`
  opacity: 0;
  position: relative;
`;

const DayLocal = styled.span`
  position: absolute;
  top: 2px;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 600;
  color: ${({ theme }) => theme.secondary.blendToBackground(800)};
  transition: opacity 0.2s ease-in-out;
`;

const DayUTC = styled(DayLocal)`
  opacity: 0;
`;

const DayDisplay = styled.div`
  position: relative;

  .icon-calendar-line {
    display: none;
  }
`;

const DateRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${({ theme }) => theme.secondary.blendToBackground(800)};

  &:hover {
    ${DateTimeLocal},
    ${DayLocal} {
      opacity: 0;
    }

    ${DateTimeUTC},
    ${DayUTC} {
      opacity: 1;
    }
  }
`;

const Balance = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px 32px;
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const SpinIcon = styled.span<{ $spin?: boolean }>`
  display: inline-flex;
  ${({ $spin }) =>
    $spin &&
    css`
      animation: ${spin} 1.2s linear infinite;
    `}
`;

const IN_PROGRESS_STATUSES = new Set(['validating', 'processing']);

const dayNumber = (iso: string, timeZoneFormat: 'local' | 'utc') =>
  formatDate(iso, { dateFormat: 'short', timeZoneFormat }).split(' ')[1].replace(',', '');

interface AchBatchHeaderProps {
  batch: ACHTransferBatch;
}

export const AchBatchHeader: React.FC<AchBatchHeaderProps> = ({ batch }) => {
  const recordLabel = batch.totalCount === 1 ? 'Record' : 'Records';
  const breakdownText = useMemo(() => buildBreakdownText(batch), [batch]);

  const status = getBatchStatusDisplay(batch);

  return (
    <Inner px="var(--page-margin)">
      <Summary>
        <Flex flexDirection="column" gap={12}>
          {renderAmounts(batch)}

          <Meta>
            <li>
              <Chip
                variant="subtle"
                type={status.type}
                icon={<SpinIcon $spin={IN_PROGRESS_STATUSES.has(batch.status)}>{status.icon}</SpinIcon>}
                size="large"
              >
                {status.label}
              </Chip>
            </li>
            <li>
              <Chip icon={<CheckScannerIcon />} size="large" variant="subtle">
                <RecordsHoverText totalText={`${batch.totalCount} ${recordLabel}`} breakdownText={breakdownText} />
              </Chip>
            </li>
            <li>
              <DateRow>
                <DayDisplay>
                  <DayLocal>{dayNumber(batch.createdAt, 'local')}</DayLocal>
                  <DayUTC>{dayNumber(batch.createdAt, 'utc')}</DayUTC>
                  <Icon.Calendar />
                </DayDisplay>
                <DateTimeDisplay>
                  <DateTimeLocal>
                    <Text size={14} color="currentColor" weight="medium" whiteSpace="nowrap">
                      {formatDateTime(batch.createdAt, { timeZoneFormat: 'local' })}
                    </Text>
                  </DateTimeLocal>
                  <DateTimeUTC>
                    <Text size={14} color="currentColor" weight="medium" whiteSpace="nowrap">
                      {formatDateTime(batch.createdAt, { timeZoneFormat: 'utc' })}
                    </Text>
                  </DateTimeUTC>
                </DateTimeDisplay>
              </DateRow>
            </li>
          </Meta>
        </Flex>
        <IdField>
          <FormElement>
            <FormLabel>Batch ID</FormLabel>
            <CopyInput value={batch.id} size="small" isMonospaced />
          </FormElement>
        </IdField>
      </Summary>
    </Inner>
  );
};

type StatColor = 'foreground' | 'success' | 'lighter';

const Stat: React.FC<{
  label: string;
  value: string;
  color?: StatColor;
}> = ({ label, value, color = 'foreground' }) => (
  <Flex flexDirection="column">
    <Flex alignItems="baseline" gap={4}>
      <Text size={24} weight="semibold" color={color} letterSpacing="tighter">
        {value}
      </Text>
      <Text size={14} color="lighter" weight="medium">
        USD
      </Text>
    </Flex>
    {label && (
      <Text size={12} color="light" weight="normal" whiteSpace="nowrap">
        {label}
      </Text>
    )}
  </Flex>
);

const placeholderAmount = (
  <Balance>
    <Text size={24} weight="semibold" color="lighter" letterSpacing="tighter">
      -
    </Text>
  </Balance>
);

const renderAmounts = (batch: ACHTransferBatch) => {
  const picked = pickBatchAmounts(batch);
  if (!picked) return placeholderAmount;

  const { phase, credit, debit } = picked;
  const isValidated = phase === 'validated';
  const hasCredit = credit > 0;
  const hasDebit = debit > 0;
  if (!hasCredit && !hasDebit) return placeholderAmount;

  const isMixed = hasCredit && hasDebit;
  const directionLabel = (direction: 'Credit' | 'Debit') =>
    isValidated ? (isMixed ? `Validated ${direction}` : 'Validated') : isMixed ? direction : '';

  return (
    <Balance>
      {hasCredit && <Stat label={directionLabel('Credit')} value={formatNumber(credit, 'USD')} color="success" />}
      {hasDebit && <Stat label={directionLabel('Debit')} value={`-${formatNumber(debit, 'USD')}`} />}
    </Balance>
  );
};
